import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, HelpOutline, AccountCircle, Payment, Build, ExpandMore, Smartphone } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

// Unsplash Image URL (Placeholder - feel free to change)
const unsplashImageUrl = 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80'; // A generic image of people working on laptops

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageContainer = styled.div`
  min-height: 100vh;
  background: #f8f9fa; // Light background for a clean look
  color: #343a40;
  animation: ${fadeIn} 0.5s ease-out;
`;

const HeroSection = styled.section`
  background-image: linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(${unsplashImageUrl});
  background-size: cover;
  background-position: center;
  padding: 8rem 2rem;
  text-align: center;
  color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;

  @media (max-width: 768px) {
    padding: 6rem 1rem;
    min-height: 50vh;
  }
`;

const HeroTitle = styled(motion.h1)`
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  animation: ${fadeInUp} 0.6s ease-out;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: 1.25rem;
  margin-bottom: 2.5rem;
  max-width: 600px;
  opacity: 0.9;
  animation: ${fadeInUp} 0.7s ease-out 0.2s;
  animation-fill-mode: backwards;


  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const SearchBarContainer = styled(motion.div)`
  display: flex;
  width: 100%;
  max-width: 700px;
  margin: 0 auto;
  background: white;
  border-radius: 50px;
  padding: 0.5rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  animation: ${fadeInUp} 0.8s ease-out 0.4s;
  animation-fill-mode: backwards;
`;

const SearchInput = styled.input`
  flex-grow: 1;
  border: none;
  outline: none;
  padding: 0.75rem 1.5rem;
  font-size: 1.1rem;
  background: transparent;
  color: #343a40;

  &::placeholder {
    color: #adb5bd;
  }
`;

const SearchButton = styled.button`
  background: #4f46e5; // Using a primary color from your Payments page
  color: white;
  border: none;
  border-radius: 50px;
  padding: 0.75rem 2rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: #4338ca;
  }
`;

const MainContent = styled.main`
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem;

  @media (max-width: 768px) {
    padding: 3rem 1rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 3rem;
  color: #212529;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const CategoriesSection = styled.section`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin-bottom: 4rem;
`;

const CategoryCard = styled(motion.div)`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.07);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 35px rgba(0, 0, 0, 0.1);
  }

  .icon {
    font-size: 3rem;
    color: #4f46e5;
    margin-bottom: 1rem;
  }

  h3 {
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 0.75rem;
    color: #343a40;
  }

  p {
    font-size: 1rem;
    color: #6c757d;
    line-height: 1.6;
  }
`;

const FAQSection = styled.section`
  margin-bottom: 4rem;
`;

const FAQItemContainer = styled(motion.div)`
  background: white;
  border-radius: 8px;
  margin-bottom: 1rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  overflow: hidden;
`;

const FAQQuestion = styled.button`
  width: 100%;
  padding: 1.5rem;
  text-align: left;
  background: white;
  border: none;
  font-size: 1.1rem;
  font-weight: 600;
  color: #343a40;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f8f9fa;
  }

  .arrow-icon {
    transition: transform 0.3s ease;
    transform: ${props => (props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  }
`;

const FAQAnswer = styled(motion.div)`
  padding: 0 1.5rem 1.5rem 1.5rem;
  font-size: 1rem;
  color: #495057;
  line-height: 1.7;
`;

const ContactCTASection = styled.section`
  text-align: center;
  padding: 3rem;
  background: #e9ecef;
  border-radius: 12px;

  h3 {
    font-size: 1.8rem;
    font-weight: 600;
    margin-bottom: 1rem;
  }

  p {
    font-size: 1.1rem;
    color: #495057;
    margin-bottom: 2rem;
  }
`;

const ContactButton = styled(motion.a)`
  display: inline-block;
  background: #28a745; // A common color for "contact" or "success"
  color: white;
  padding: 0.8rem 2.5rem;
  border-radius: 50px;
  text-decoration: none;
  font-weight: 600;
  font-size: 1.1rem;
  transition: background-color 0.2s ease, transform 0.2s ease;

  &:hover {
    background: #218838;
    transform: translateY(-2px);
  }
`;

const helpCategories = [
  { title: "Getting Started", description: "New to ServTrackr? Find setup guides and basic tutorials here.", icon: <HelpOutline className="icon" />, link: "/help/getting-started" },
  { title: "Account & Billing", description: "Manage your subscription, payment methods, and view invoices.", icon: <AccountCircle className="icon" />, link: "/help/account-billing" },
  { title: "Using ServTrackr Pay", description: "Learn about payment processing, features, and best practices.", icon: <Payment className="icon" />, link: "/help/payments" },
  { title: "Troubleshooting", description: "Find solutions to common issues and technical problems.", icon: <Build className="icon" />, link: "/help/troubleshooting" },
  { title: "iOS App", description: "Guides and support for the ServTrackr iOS app.", icon: <Smartphone className="icon" />, link: "/resources/help-center/ios" },
];

const faqsData = [
  { question: "How do I reset my password?", answer: "You can reset your password by clicking on the 'Forgot Password' link on the login page. Follow the instructions sent to your email address." },
  { question: "Where can I find my invoices?", answer: "Your invoices are available in the 'Billing' section of your account dashboard. You can view and download them from there." },
  { question: "What payment methods are supported by ServTrackr Pay?", answer: "ServTrackr Pay supports a wide range of payment methods, including major credit cards (Visa, Mastercard, Amex), ACH transfers, and digital wallets like Apple Pay and Google Pay." },
  { question: "Why is my payment failing?", answer: "Payment failures can occur due to several reasons, such as incorrect card details, insufficient funds, or bank restrictions. Please double-check your information or contact your bank." },
  { question: "How do I integrate ServTrackr with other tools?", answer: "ServTrackr offers various integrations. You can find detailed guides in our 'Integrations' section in the help documentation or contact support for specific requests." },
];


const Helpdesk = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [openFAQ, setOpenFAQ] = useState(null);
  const navigate = useNavigate();

  const pageTitle = "ServTrackr Help Desk | Support & FAQ";
  const pageDescription = "Find answers to your questions, explore guides, and get the support you need with ServTrackr. Browse help topics, FAQs, and more.";
  const pageImage = "https://servtrackr.com/images/helpdesk-preview.png"; // <-- REPLACE with actual image URL
  const pageUrl = "https://servtrackr.com/helpdesk"; // <-- REPLACE with actual URL

  const handleSearch = (e) => {
    e.preventDefault();
    // Implement search logic here, e.g., redirect to a search results page
    console.log("Searching for:", searchTerm);
    alert(`Search functionality for "${searchTerm}" is not implemented yet.`);
  };

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  return (
    <PageContainer>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        {/* Open Graph / Facebook */} 
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={pageImage} />
        {/* Twitter */} 
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={pageUrl} />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={pageImage} />
      </Helmet>
      <HeroSection>
        <HeroTitle
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          How can we help you?
        </HeroTitle>
        <HeroSubtitle
           initial={{ opacity: 0, y: 20 }}
           animate={{ opacity: 1, y: 0 }}
           transition={{ duration: 0.6, delay: 0.2 }}
        >
          Find answers to your questions, explore guides, and get the support you need.
        </HeroSubtitle>
        <SearchBarContainer
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <SearchInput
            type="text"
            placeholder="Search for articles, guides, and more..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <SearchButton onClick={handleSearch}>
            <Search style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            Search
          </SearchButton>
        </SearchBarContainer>
      </HeroSection>

      <MainContent>
        <SectionTitle>Browse Help Topics</SectionTitle>
        <CategoriesSection>
          {helpCategories.map((category, index) => (
            <CategoryCard
              key={index}
              onClick={() => navigate(category.link)}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ scale: 1.03 }}
            >
              {category.icon}
              <h3>{category.title}</h3>
              <p>{category.description}</p>
            </CategoryCard>
          ))}
        </CategoriesSection>

        <SectionTitle>Frequently Asked Questions</SectionTitle>
        <FAQSection>
          {faqsData.map((faq, index) => (
            <FAQItemContainer 
              key={index}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
            >
              <FAQQuestion onClick={() => toggleFAQ(index)} isOpen={openFAQ === index}>
                {faq.question}
                <ExpandMore className="arrow-icon" />
              </FAQQuestion>
              <AnimatePresence>
                {openFAQ === index && (
                  <FAQAnswer
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                  >
                    {faq.answer}
                  </FAQAnswer>
                )}
              </AnimatePresence>
            </FAQItemContainer>
          ))}
        </FAQSection>

        <ContactCTASection>
          <h3>Still need help?</h3>
          <p>Our support team is ready to assist you. Reach out to us for any further questions.</p>
          <ContactButton 
            href="/contact" // Assuming you have a contact page
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
          >
            Contact Support
          </ContactButton>
        </ContactCTASection>
      </MainContent>
    </PageContainer>
  );
};

export default Helpdesk;
